import React, {
  useCallback,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  NotificationsContext,
  NotificationsDispatch,
} from "@src/context/NotificationsContext";
import NotificationItem from "./NotificationItem";
import useApiHook from "@src/hooks/useApiHook";
import useApiError from "@src/hooks/useApiError";
import { useNavigate } from "react-router-dom";
import useNotification from "@src/hooks/useNotification";
import { UserContext, UserDispatchContext } from "@src/context/UserContext";
import NotificationSkeleton from "../Skeleton/Notifications/NotificationSkeleton";
import useReactQuery from "@src/hooks/useReactQuery";
import { AuthContext } from "@src/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import type { INotificationItem } from "@src/types/AppTypes";

const NotificationDropdown: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setApiError = useApiError();
  const user = useContext(UserContext);
  const isAuth = useContext(AuthContext);
  const markAsReadEndpoint = useApiHook({
    url: "/notifications/",
    method: "post",
  });
  const clearAllEndpoint = useApiHook({
    method: "delete",
    url: "/notifications/delete_notifications",
  });
  const notificationsEndpoint = useApiHook({
    method: "get",
    url: "/users/user_notifications",
  });
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const updateUser = useContext(UserDispatchContext);
  const { notifications } = useContext(NotificationsContext);
  const setNotifications = useContext(NotificationsDispatch);
  const [showNotifications, setShowNotifications] = useState<boolean>(false);
  const flashNotification = useNotification();

  const markAsRead = useCallback(
    async (id: number, index: number) => {
      setShowNotifications(false);
      const n = [...notifications];
      n[index].is_read = !n[index].is_read;
      try {
        const res = await markAsReadEndpoint<{
          status: boolean;
          message: boolean;
          total_notifications: number;
        }>(null, id + "/mark_as_read");

        if (res?.data) {
          if (user) {
            queryClient.invalidateQueries({
              queryKey: ["get-all-notifications"],
            });
            updateUser({
              type: "updateUser",
              payload: {
                ...user,
                total_notifications: res?.data.total_notifications,
              },
            });
          }
        }
      } catch (e) {
        setApiError(e);
        n[index].is_read = !n[index].is_read;
      } finally {
        setNotifications({ type: "updateNotifications", payload: n });
      }
    },
    [
      setApiError,
      markAsReadEndpoint,
      notifications,
      setNotifications,
      updateUser,
      user,
      queryClient,
    ]
  );

  const clearAll = useCallback(async () => {
    try {
      setIsClearing(true);
      const res = await clearAllEndpoint<{
        status: boolean;
        message: string;
      }>();
      if (res?.data.status) {
        flashNotification(res.data);
        queryClient.invalidateQueries({ queryKey: ["get-all-notifications"] });
        setNotifications({ type: "updateNotifications", payload: [] });

        if (user) {
          updateUser({
            type: "updateUser",
            payload: { ...user, total_notifications: 0 },
          });
        }
      }
    } catch (e) {
      setApiError(e);
    } finally {
      setIsClearing(false);
    }
  }, [
    setApiError,
    clearAllEndpoint,
    setNotifications,
    flashNotification,
    updateUser,
    user,
    queryClient,
  ]);

  const showNotificationsDot = useMemo(() => {
    for (const value of notifications) {
      if (value.is_read === false) {
        return true;
      }
    }
    if (user && user.total_notifications !== 0) {
      return true;
    }
    return false;
  }, [notifications, user]);

  const {
    data: notificationData,
    isSuccess: notificationIsSuccess,
    error: notificationError,
    refetch: notificationRefetch,
    isLoading: notificationIsLoading,
  } = useReactQuery<{
    status: boolean;
    user_notifications: INotificationItem[];
  }>({
    args: { queryKey: ["get-all-notifications"], enabled: isAuth },
    queryFn: () => notificationsEndpoint(null, "", null),
  });

  useEffect(() => {
    if (notificationIsSuccess && notificationData) {
      setNotifications({
        type: "updateNotifications",
        payload: notificationData.data.user_notifications,
      });
    } else if (notificationError) {
      setApiError(notificationError);
    }
  }, [
    notificationIsSuccess,
    notificationError,
    setNotifications,
    notificationData,
    setApiError,
  ]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!event.target) return;
      const target = event.target as HTMLElement;
      if (target.closest(".notification-icon")) return; // this is for if user click on same notification icon, then prevent to close.
      if (!target.closest(".notification-dropdown")) {
        setShowNotifications(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative">
      <div>
        <button
          className="w-14 h-14 max-xl:w-[48px] max-xl:h-[48px] grid place-items-center border-2 text-2xl max-xl:text-xl border-blue rounded-full bg-radial-gradient duration-150 hover:scale-95 notification-icon"
          onClick={(e) => {
            e.preventDefault();
            if (!showNotifications) {
              if (notifications.length === 0) {
                notificationRefetch();
              }
            }
            setShowNotifications(!showNotifications);
          }}
          aria-label="Notification"
        >
          <i className="ri-notification-line"></i>
        </button>
        {showNotificationsDot && (
          <div className="w-3 h-3 rounded-full bg-primary absolute top-1 right-1 max-md:top-[1px]"></div>
        )}
      </div>
      <div className="relative z-[3]">
        <div
          className={`${
            !showNotifications ? "hidden" : ""
          } notification-dropdown top-5 min-w-[35rem] max-h-[400px] max-sm:border-4 max-sm:border-secondary max-sm:min-w-[calc(100vw-30px)] absolute -right-10 max-md:-right-[4rem] z-[1] bg-gradient-to-t from-[#184AF4] to-blue rounded-[20px_20px_20px_20px] min-h-[554px] flex flex-col`}
        >
          <div className="size-full -z-[1] bg-[100%_100%] bg-no-repeat bg-cover bg-[url('@assets/images/borders/DiscussionBorder.webp')] absolute max-sm:bg-none"></div>
          <div className="p-5 pb-3 max-md:p-4 max-md:pb-0">
            <div className="flex items-center justify-between gap-4 text-primary w-full text-start">
              <h4 className="text-2xl max-md:text-xl max-sm:text-lg">
                Notifications
              </h4>
              <button
                disabled={isClearing || notifications.length === 0}
                className="flex text-lg disabled:opacity-50 disabled:hover:cursor-not-allowed"
                onClick={clearAll}
                aria-label="Clear All"
              >
                {isClearing ? (
                  <i className="ri-loop-right-line btn-loader"></i>
                ) : (
                  "Clear All"
                )}
              </button>
            </div>
          </div>
          <div className="me-1.5 overflow-y-auto mb-5 flex-1 flex flex-col items-start justify-start">
            {notificationIsLoading ? (
              <NotificationSkeleton />
            ) : notifications.length > 0 ? (
              <ul className="grid grid-cols-1 gap-4 px-5 relative w-full">
                {notifications.map((item, itemIndex) => {
                  return (
                    <NotificationItem
                      key={item.id}
                      item={item}
                      markAsRead={markAsRead}
                      index={itemIndex}
                      navigate={navigate}
                      setShowNotifications={setShowNotifications}
                    />
                  );
                })}
              </ul>
            ) : (
              <div className="flex size-full flex-1 items-center justify-center">
                <span className="text-xl max-md:text-lg text-primary text-center">
                  You don't have any new notifications
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* <div
        className={`${
          !showNotifications ? "hidden" : ""
        } fixed top-0 left-0 size-full z-[2]`}
      ></div> */}
    </div>
  );
});

export default NotificationDropdown;
